import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { Component, useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import request from 'superagent';
import { trackEvent } from '@zillow/universal-analytics';
import { ValidationMessage, FormHelpError, TermsOfUse, formUtils } from '@zillow/reg-login-components';
import { Popper, VisuallyHidden, Text, Popover, Heading, Paragraph, spaceMixin, FormField, Label, AdornedInput, Input, Adornment, Form, FormActions, Button, InlineFeedback } from '@zillow/constellation';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/esm/taggedTemplateLiteralLoose';
import styled from 'styled-components';
import _inheritsLoose from '@babel/runtime/helpers/esm/inheritsLoose';
import _debounce from 'lodash/debounce';
import { Donut } from '@zillow/zrs-design-systems';

var apiPath = '/user/account/services';
var RESET_PASSWORD_POST = '/myzillow/api/account/passwordreset';
var CREATE_PASSWORD_POST = apiPath + "/UpdatePassword.htm";
var PASSWORD_STRENGTH_ENDPOINT = apiPath + "/Strength.htm";
var SPECIAL_CHARACTER_REGEX = /[()~!@#$%^&*_\-+=`|\\{}[\]:;"'<>,./?]/;
var UPPERCASE_CHARACTER_REGEX = /[A-Z]/;
var LOWERCASE_CHARACTER_REGEX = /[a-z]/;

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n    display: flex;\n    align-items: center;\n    background: none;\n    border: none;\n    padding: 0;\n    cursor: pointer;\n\n    svg {\n        height: 22px;\n        width: 22px;\n    }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
var METER_CONFIG = {
  null: {
    color: '#FFFFFF',
    text: '',
    percent: 0
  },
  0: {
    color: '#F10000',
    text: 'Poor',
    percent: 0
  },
  1: {
    color: '#F10000',
    text: 'Weak',
    percent: 25
  },
  2: {
    color: '#FF6E01',
    text: 'Fair',
    percent: 50
  },
  3: {
    color: '#3ACE09',
    text: 'Good',
    percent: 75
  },
  4: {
    color: '#3ACE09',
    text: 'Excellent',
    percent: 100
  }
};

function getPasswordStrength(password) {
  return new Promise(function (resolve, reject) {
    if (!password || password.length === 0) {
      return reject(new Error('Password too short'));
    }

    return request.post(PASSWORD_STRENGTH_ENDPOINT).type('form').send({
      password: password
    }).end(function (err, res) {
      if (err) {
        return reject(err);
      }

      return resolve(res.body.score);
    });
  });
}

var TOOLTIP_MESSAGE = 'Good passwords are hard to guess. Use uncommon words, non-standard uPPercasing, numb3rs or symbol$ in place of letters, or creative spelling.';
var StrengthMeterButton = styled.button(_templateObject()); // TODO (ACT-1601): move Overlay to its own component and add unit tests

var Overlay = function Overlay(props) {
  var _ref = METER_CONFIG[props.score] || {},
      color = _ref.color,
      text = _ref.text,
      percent = _ref.percent;

  var popover = /*#__PURE__*/React.createElement(Popover, {
    header: /*#__PURE__*/React.createElement(Heading, {
      level: 3
    }, text || 'Password strength'),
    body: /*#__PURE__*/React.createElement(Paragraph, null, TOOLTIP_MESSAGE)
  });
  return /*#__PURE__*/React.createElement(Popper, {
    triggered: popover
  }, /*#__PURE__*/React.createElement(StrengthMeterButton, {
    type: "button"
  }, /*#__PURE__*/React.createElement(VisuallyHidden, {
    as: "span"
  }, "password strength information"), /*#__PURE__*/React.createElement(Text, {
    fontType: "finePrint",
    marginRight: 0.5
  }, text), /*#__PURE__*/React.createElement(Donut, {
    percent: percent,
    width: 15,
    color: color,
    animationDuration: 300,
    animationDelay: 100
  })));
};

Overlay.propTypes = process.env.NODE_ENV !== "production" ? {
  score: PropTypes.number
} : {};
Overlay.defaultProps = {
  score: null
}; // eslint-disable-next-line zillow/react/no-multi-comp

var StrengthMeter = /*#__PURE__*/function (_Component) {
  _inheritsLoose(StrengthMeter, _Component);

  function StrengthMeter() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.updatePasswordStrength = _debounce(function (newPassword) {
      getPasswordStrength(newPassword).then(function (score) {
        // eslint-disable-next-line zillow/react/no-unused-state
        _this.setState({
          score: score
        });
      });
    }, 500);
    return _this;
  }

  var _proto = StrengthMeter.prototype;

  _proto.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    if (this.props.password !== nextProps.password) {
      this.updatePasswordStrength(nextProps.password);
    }
  };

  _proto.render = function render() {
    var _ref2 = this.state || {},
        score = _ref2.score;

    return /*#__PURE__*/React.createElement("div", {
      className: "password-strength-meter"
    }, /*#__PURE__*/React.createElement(Overlay, {
      score: score
    }));
  };

  return StrengthMeter;
}(Component);

StrengthMeter.propTypes = process.env.NODE_ENV !== "production" ? {
  password: PropTypes.string.isRequired
} : {};

function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n    margin-top: ", ";\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}
var ValidationMessages = styled.ul(_templateObject$1(), spaceMixin('xs'));

var PasswordInput = function PasswordInput(_ref) {
  var value = _ref.value,
      validationMessages = _ref.validationMessages,
      onChange = _ref.onChange,
      onBlur = _ref.onBlur,
      showInputAsInvalid = _ref.showInputAsInvalid;
  var validationMarkup = null;
  var passwordInstructionIds = null;
  var passwordValidation = false;

  if (validationMessages) {
    passwordValidation = true;
    validationMarkup = validationMessages.map(function (_ref2, i) {
      var validated = _ref2.validated,
          message = _ref2.message;
      passwordValidation = validated ? passwordValidation : false;
      var status = 'pass';

      if (!validated && value) {
        status = 'fail';
      } else if (!validated && !value) {
        status = 'neutral';
      }

      return message ? /*#__PURE__*/React.createElement("li", {
        key: i
      }, /*#__PURE__*/React.createElement(ValidationMessage, {
        text: message,
        status: status,
        keyCount: i
      })) : null;
    });
  }

  if (typeof document !== 'undefined') {
    var idList = [];

    if (document.getElementById('validation-text-2')) {
      idList.push('validation-text-2');
    }

    if (document.getElementById('validation-text-3')) {
      idList.push('validation-text-3');
    }

    if (document.getElementById('validation-text-4')) {
      idList.push('validation-text-4');
    }

    if (document.getElementById('validation-text-5')) {
      idList.push('validation-text-5');
    }

    passwordInstructionIds = idList.length ? idList.join(' ') : null;
  }

  return /*#__PURE__*/React.createElement(FormField, {
    error: showInputAsInvalid,
    label: /*#__PURE__*/React.createElement(Label, {
      htmlFor: "newPassword"
    }, "Password"),
    control: /*#__PURE__*/React.createElement(AdornedInput, {
      input: /*#__PURE__*/React.createElement(Input, {
        type: "password",
        id: "newPassword",
        placeholder: "Create password",
        errorText: "",
        onChange: onChange,
        onBlur: onBlur,
        defaultValue: value,
        "aria-describedby": passwordInstructionIds,
        required: true
      }),
      rightAdornment: /*#__PURE__*/React.createElement(Adornment, {
        className: "adornment"
      }, /*#__PURE__*/React.createElement(StrengthMeter, {
        password: value,
        validated: passwordValidation
      }))
    }),
    description: /*#__PURE__*/React.createElement(ValidationMessages, null, validationMarkup)
  });
};

PasswordInput.propTypes = process.env.NODE_ENV !== "production" ? {
  value: PropTypes.string.isRequired,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  validationMessages: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  showInputAsInvalid: PropTypes.bool
} : {};
PasswordInput.defaultProps = {
  onBlur: null,
  showInputAsInvalid: false
};

/**
 *  Validates that input is not more than 128 characters
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateMaxLength = function validateMaxLength(v) {
  var validated = /^.{0,128}$/.test(v);
  return {
    validated: validated,
    message: validated ? '' : 'Cannot exceed 128 characters'
  };
};
/**
 *  Validates that input is 8 characters or more
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateLength = function validateLength(v) {
  return {
    validated: /^.{8,}$/.test(v),
    message: 'At least 8 characters'
  };
};
/**
 * Checks if input has at least 1 number and 1 letter, validation message updates based on what's
 * needed
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateAlphaNumeric = function validateAlphaNumeric(v) {
  var validated = /^(?=.*[0-9])(?=.*[a-zA-Z])(.+)$/.test(v);
  var message;

  if (!validated && /^[a-zA-Z]+$/.test(v)) {
    message = 'At least 1 number';
  } else if (!validated && /^[0-9]+$/.test(v)) {
    message = 'At least 1 letter';
  } else {
    message = 'Mix of letters and numbers';
  }

  return {
    validated: validated,
    message: message
  };
};
/**
 * Checks if input has at least 1 special character
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateSpecialCharacters = function validateSpecialCharacters(v) {
  var validated = SPECIAL_CHARACTER_REGEX.test(v);
  var message = !validated ? 'At least 1 special character' : 'Contains a special character';
  return {
    validated: validated,
    message: message
  };
};
/**
 * Checks if input has a mix of uppercase and lowercase letter
 * @param {*} v
 *            input value
 * @return {Object} input value validation and validation message
 */

var validateCapitalization = function validateCapitalization(v) {
  var uppercaseValidated = UPPERCASE_CHARACTER_REGEX.test(v);
  var lowercaseValidated = LOWERCASE_CHARACTER_REGEX.test(v);
  var validated = uppercaseValidated && lowercaseValidated;
  var message;

  if (!lowercaseValidated) {
    message = 'At least 1 lowercase letter';
  }

  if (!uppercaseValidated) {
    if (message) {
      message += ' and 1 uppercase letter';
    } else {
      message = 'At least 1 uppercase letter';
    }
  }

  message = validated ? 'Mix of uppercase and lowercase letters' : message;
  return {
    validated: validated,
    message: message
  };
};
/**
 * Checks if a value is equal to the email address or its alias (alias@domain)
 * @param {String} value
 *                  value to check against
 * @param {String} email
 *                  email address
 * @return {Boolean} value isn't equal to email or alias
 */

var validateValueNotEmailOrAlias = function validateValueNotEmailOrAlias(value, email) {
  var lowerCaseValue = typeof value === 'string' ? value.toLowerCase() : '';
  var lowerCaseEmail = typeof email === 'string' ? email.toLowerCase() : '';
  var alias = /^(.+)@/.exec(lowerCaseEmail);
  return lowerCaseValue !== lowerCaseEmail && (alias === null || lowerCaseValue !== alias[1].toLowerCase());
};
/**
 * Get the input value from a redux-input managed input object. returns value or error as fallback
 * @param {Object} inputObject {value, error}
 * @return {String} input value
 */

var getValueFromInputStateObject = function getValueFromInputStateObject(_ref) {
  var inputValue = _ref.value,
      fallbackInputValue = _ref.error;
  return inputValue || fallbackInputValue;
};
/**
 * Checks if input does not match the email address or email address alias
 * @param {*} v
 *                  input value
 * @param {Object} newEmail
 *                  inputs with newEmail value
 * @return {Object} input value validation and validation message
 */

var validatePasswordNotEmail = function validatePasswordNotEmail(password, _ref2) {
  var _ref2$newEmail = _ref2.newEmail,
      newEmail = _ref2$newEmail === void 0 ? {} : _ref2$newEmail;
  var email = getValueFromInputStateObject(newEmail);
  var validated = validateValueNotEmailOrAlias(password, email);
  return {
    validated: validated,
    message: validated || !password ? '' : 'Cannot match your email address or alias'
  };
};
var PASSWORD_VALIDATORS = [validatePasswordNotEmail, validateMaxLength, validateLength, validateAlphaNumeric, validateSpecialCharacters, validateCapitalization];
/**
 * @typedef {Object} InputValidation
 * @property {Boolean} valid - true if the input passes validation
 * @property {Array} messages - list of validation messages
 */

/**
 * Redux inputs validator for the newPassword field. Validates against the array of
 * validators defined, and dispatches the validationMessages updates to the store
 *
 * @param {String} input
 *                  password field's value
 * @param {Object} inputs
 *                  The store's inputs state. Used to cross validate the password with email
 *
 * @return {Object} InputValidation

 */

var validatePassword = function validatePassword(input, inputs) {
  var validationMessages = [];
  var finalValidation = true;
  var validators = PASSWORD_VALIDATORS;
  validators.forEach(function (validator) {
    var result = validator(input, inputs);
    validationMessages.push(result);
    finalValidation = finalValidation && result.validated;
  });
  return {
    valid: finalValidation,
    messages: validationMessages
  };
};

var COMPONENT_STATE = {
  error: 'error',
  ready: 'ready',
  sending: 'sending'
};

var CreatePassword = function CreatePassword(_ref) {
  var authToken = _ref.authToken,
      emailAddress = _ref.emailAddress,
      formSubmissionHandler = _ref.formSubmissionHandler,
      gaLabel = _ref.gaLabel,
      introText = _ref.introText,
      passwordLessUser = _ref.passwordLessUser,
      successHandler = _ref.successHandler;

  var _useState = useState(COMPONENT_STATE.ready),
      _useState2 = _slicedToArray(_useState, 2),
      status = _useState2[0],
      setStatus = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      password = _useState4[0],
      setPassword = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      passwordBlurred = _useState6[0],
      setPasswordBlurred = _useState6[1];

  var _useState7 = useState(''),
      _useState8 = _slicedToArray(_useState7, 2),
      confirmPassword = _useState8[0],
      setConfirmPassword = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      confirmPasswordBlurred = _useState10[0],
      setConfirmPasswordBlurred = _useState10[1];

  var _useState11 = useState(''),
      _useState12 = _slicedToArray(_useState11, 2),
      errorText = _useState12[0],
      setErrorText = _useState12[1];

  var _useState13 = useState(validatePassword(password, {
    newEmail: emailAddress
  })),
      _useState14 = _slicedToArray(_useState13, 2),
      validation = _useState14[0],
      setValidation = _useState14[1];

  useEffect(function () {
    setValidation(validatePassword(password, {
      newEmail: emailAddress
    }));
  }, [password, confirmPassword, emailAddress]);

  var validPasswordConfirmation = function validPasswordConfirmation() {
    return password === confirmPassword;
  };

  var handleSubmit = function handleSubmit() {
    if (!validation.valid || !validPasswordConfirmation()) {
      return;
    }

    var formData = new FormData();
    formData.append('token', authToken);
    formData.append('password', password);
    formData.append('passwordless', passwordLessUser);
    setStatus(COMPONENT_STATE.sending);

    if (formSubmissionHandler) {
      formSubmissionHandler({
        password: password
      });
    } else {
      request.post(CREATE_PASSWORD_POST).send(formData).end(function (err, res) {
        if (err || !res.ok) {
          setStatus(COMPONENT_STATE.error);
          setErrorText('Error. Please try again.');
        } else {
          trackEvent({
            category: 'auth',
            action: 'passwordsubmit//',
            label: gaLabel
          });
          setStatus(COMPONENT_STATE.ready);
          successHandler();
        }
      });
    }
  };

  return /*#__PURE__*/React.createElement(Form, {
    onSubmit: function onSubmit(e) {
      e.preventDefault();
      handleSubmit();
    }
  }, introText && /*#__PURE__*/React.createElement(Paragraph, {
    marginBottom: "sm"
  }, introText), /*#__PURE__*/React.createElement(Input, {
    type: "hidden",
    value: authToken
  }), /*#__PURE__*/React.createElement(PasswordInput, {
    onChange: function onChange(e) {
      setPassword(e.target.value);
    },
    onBlur: function onBlur() {
      setPasswordBlurred(true);
    },
    validationMessages: validation.messages,
    showInputAsInvalid: passwordBlurred && !validation.valid,
    value: password
  }), /*#__PURE__*/React.createElement(FormField, {
    label: /*#__PURE__*/React.createElement(Label, {
      htmlFor: "confirmPassword"
    }, "Confirm password"),
    control: /*#__PURE__*/React.createElement(Input, {
      type: "password",
      id: "confirmPassword",
      placeholder: "Confirm password",
      onChange: function onChange(e) {
        setConfirmPassword(e.target.value);
      },
      onBlur: function onBlur() {
        setConfirmPasswordBlurred(true);
      },
      defaultValue: confirmPassword,
      required: true
    }),
    description: confirmPasswordBlurred && !validPasswordConfirmation() && /*#__PURE__*/React.createElement(FormHelpError, {
      text: "Passwords do not match"
    }),
    error: confirmPasswordBlurred && !validPasswordConfirmation()
  }), /*#__PURE__*/React.createElement(FormActions, {
    className: "form-actions"
  }, /*#__PURE__*/React.createElement(Button, {
    buttonType: "primary",
    type: "submit",
    fluid: true,
    disabled: !validation.valid || !validPasswordConfirmation()
  }, status === COMPONENT_STATE.sending ? 'Sending' : 'Submit')), status === COMPONENT_STATE.error && errorText && /*#__PURE__*/React.createElement(FormHelpError, {
    text: errorText
  }), /*#__PURE__*/React.createElement(TermsOfUse, null));
};

CreatePassword.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Auth token needed for the register/auth endpoint
   */
  authToken: PropTypes.string.isRequired,

  /**
   * The email address for the account whose password is being created.
   */
  emailAddress: PropTypes.string.isRequired,

  /**
   * Optional handler when the form is submitted. By default the form when send the form fields
   * to an endpoint on Zillow.com. If a different endpoint is needed,
   */
  formSubmissionHandler: PropTypes.func,

  /**
   * GA Label for our events
   */
  gaLabel: PropTypes.string.isRequired,

  /**
   * Text to be rendered at the top of the form
   */
  introText: PropTypes.any,

  /**
   * Indicates that the create password form is for a Freepass (AKA passwordless user)
   */
  passwordLessUser: PropTypes.bool,

  /**
   * Function to be executed upon successful password creation
   */
  successHandler: PropTypes.func
} : {};
CreatePassword.defaultProps = {
  formSubmissionHandler: null,
  introText: 'Create a password for your Zillow account.',
  passwordLessUser: false,
  successHandler: function successHandler() {}
};

var INTRO_TEXT = 'For security, confirm that you own this account by clicking the link in the email we just sent you and then update your password.';
var COMPONENT_STATE$1 = {
  sending: 'sending',
  sent: 'sent',
  error: 'error'
};

var PasswordRecovery = function PasswordRecovery(_ref) {
  var email = _ref.email,
      resetPasswordLinkUrl = _ref.resetPasswordLinkUrl,
      hasSentResetPWEmail = _ref.hasSentResetPWEmail,
      sendResetPWEmailFn = _ref.sendResetPWEmailFn,
      newLaneTrack = _ref.newLaneTrack;

  var _useState = useState(resetPasswordLinkUrl && !hasSentResetPWEmail ? COMPONENT_STATE$1.sending : COMPONENT_STATE$1.sent),
      _useState2 = _slicedToArray(_useState, 2),
      status = _useState2[0],
      setStatus = _useState2[1];

  var resetPassword = function resetPassword() {
    var data = {
      emailTypeId: 608,
      emailLink: resetPasswordLinkUrl
    };
    setStatus(COMPONENT_STATE$1.sending); // call sendResetPWEmailFn if provided, otherwise use request

    if (sendResetPWEmailFn && typeof sendResetPWEmailFn === 'function') {
      sendResetPWEmailFn().then(function (success) {
        if (success) {
          setStatus(COMPONENT_STATE$1.sent);
        } else {
          setStatus(COMPONENT_STATE$1.error);
        }
      }).catch(function () {
        return setStatus(COMPONENT_STATE$1.error);
      });
    } else {
      request.post(RESET_PASSWORD_POST).send(formUtils.serialize(data)).end(function (err, res) {
        if (err || !res.ok) {
          setStatus(COMPONENT_STATE$1.error);
        } else {
          setStatus(COMPONENT_STATE$1.sent);
        }
      });
    }
  };

  useEffect(function () {
    if (newLaneTrack && typeof newLaneTrack === 'function') {
      newLaneTrack();
    }

    if (resetPasswordLinkUrl && !hasSentResetPWEmail) {
      resetPassword();
    }
  }, [hasSentResetPWEmail, newLaneTrack]);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("p", null, INTRO_TEXT), status === COMPONENT_STATE$1.error && /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(InlineFeedback, {
    appearance: "error"
  }, "There was an issue sending the reset email. Please try again."), /*#__PURE__*/React.createElement(Button, {
    fluid: true,
    size: "sm",
    marginBottom: "sm",
    onClick: resetPassword
  }, "Try Again")), status === COMPONENT_STATE$1.sent && /*#__PURE__*/React.createElement("p", null, "Email sent", email && /*#__PURE__*/React.createElement(Fragment, null, ' ', "to: ", /*#__PURE__*/React.createElement("strong", null, email))), status === COMPONENT_STATE$1.sending && /*#__PURE__*/React.createElement("p", null, "Email is being sent"));
};

PasswordRecovery.propTypes = process.env.NODE_ENV !== "production" ? {
  email: PropTypes.string.isRequired,
  resetPasswordLinkUrl: PropTypes.string,
  hasSentResetPWEmail: PropTypes.bool,
  sendResetPWEmailFn: PropTypes.func,
  newLaneTrack: PropTypes.func
} : {};
PasswordRecovery.defaultProps = {
  resetPasswordLinkUrl: null
};
PasswordRecovery.headerText = 'Check your email';

export { CreatePassword, PasswordInput, PasswordRecovery, StrengthMeter };
